import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { RollingText } from './RollingText';
import { numberAgnosticKey } from './utils';

export interface ReasoningStep {
    text: string;
}

interface ReasoningProps {
    steps: ReasoningStep[];
    summary?: string[];
    isThinking?: boolean;
    className?: string;
    icon?: ReactNode;
    animatePhraseChange?: boolean;
    defaultOpen?: boolean;
}

export function Reasoning({
    steps,
    summary,
    isThinking = false,
    className,
    icon,
    animatePhraseChange = false,
    defaultOpen = false,
}: ReasoningProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const [open, setOpen] = useState(defaultOpen);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [iconVisible, setIconVisible] = useState(false);
    const defaultOpenRef = useRef(defaultOpen);
    defaultOpenRef.current = defaultOpen;

    useEffect(() => {
        if (!isThinking) return;

        setIconVisible(true);
        setOpen(false);
        const startTime = Date.now();

        return () => {
            setDurationSeconds(
                Math.round((Date.now() - startTime) / MILLISECONDS_TO_SECONDS),
            );
            setOpen(defaultOpenRef.current);
        };
    }, [isThinking]);

    const activeStep = steps[steps.length - 1];

    if (
        (!summary || summary.length === 0) &&
        steps.length === 0 &&
        !isThinking
    ) {
        return null;
    }

    if (isThinking) {
        return (
            <div className={cn('w-full', className)}>
                <div className="flex items-center gap-2 pb-1.5 text-sm text-grey-300">
                    {icon && (
                        <span className="flex items-center justify-center">
                            {icon}
                        </span>
                    )}
                    {animatePhraseChange ? (
                        <span
                            key={numberAgnosticKey(
                                activeStep?.text || t('reasoning.searching'),
                            )}
                            className="inline-block animate-fadeIn"
                        >
                            <span className="text-grey-200 animate-shimmer">
                                <RollingText
                                    text={
                                        activeStep?.text ||
                                        t('reasoning.searching')
                                    }
                                />
                            </span>
                        </span>
                    ) : (
                        <span className="text-grey-200 animate-shimmer">
                            <RollingText
                                text={
                                    activeStep?.text || t('reasoning.searching')
                                }
                            />
                        </span>
                    )}
                </div>
            </div>
        );
    }

    const displayLabel =
        activeStep?.text ||
        t('reasoning.searchedFor', { seconds: durationSeconds });
    return (
        <div className={cn('w-full', className)}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                disabled={!summary || summary.length === 0}
                className={cn(
                    'group flex items-center gap-2 rounded-md pb-1.5 text-left text-sm text-grey-200 transition-colors',
                    summary && summary.length && 'hover:text-grey-100',
                )}
            >
                {icon && iconVisible && (
                    <span
                        className="flex items-center justify-center animate-icon-fade-out"
                        onAnimationEnd={() => setIconVisible(false)}
                    >
                        {icon}
                    </span>
                )}
                <span className="text-sm">
                    <RollingText text={displayLabel} />
                </span>
                {summary && summary.length && (
                    <CaretDownIcon
                        size={12}
                        className={cn(
                            'transition-transform duration-200',
                            open && 'rotate-180',
                        )}
                    />
                )}
            </button>

            {summary && summary.length > 0 && (
                <div
                    className={cn(
                        'grid transition-[grid-template-rows] duration-300 ease-in-out',
                        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                >
                    <div className="overflow-hidden">
                        <div className="max-h-[300px] overflow-y-auto scrollbar-hide border-l-2 border-grey-600 ml-3 pl-3 py-2">
                            <div className="flex flex-col gap-3">
                                {durationSeconds > 0 && (
                                    <p className="text-sm italic leading-relaxed text-grey-400">
                                        {t('reasoning.searchedFor', {
                                            seconds: durationSeconds,
                                        })}
                                    </p>
                                )}
                                {summary?.map((sentence, index) => (
                                    <p
                                        key={index}
                                        className="text-sm italic leading-relaxed text-grey-400"
                                    >
                                        {sentence}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
